// src/contexts/AttendanceContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import attendanceService from '../services/attendance.service';
import { sessionCache } from '../utils/sessionCache';
import { getCurrentISTDate, toISTDateString } from '../utils/dateHelpers';

const AttendanceContext = createContext();

export const useAttendance = () => {
    const context = useContext(AttendanceContext);
    if (!context) {
        throw new Error('useAttendance must be used within an AttendanceProvider');
    }
    return context;
};

// Removed localStorage - all data from backend only

export const AttendanceProvider = ({ children }) => {
    const [attendanceRecords, setAttendanceRecords] = useState({});
    const [attendanceSnapshots, setAttendanceSnapshots] = useState({});
    const [attendanceRequests, setAttendanceRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch attendance on mount if authenticated
    useEffect(() => {
        const authToken = localStorage.getItem('authToken');
        const user = localStorage.getItem('user');

        if (authToken && user) {
            try {
                const userData = JSON.parse(user);
                if (userData.id) {
                    //console.log('🔄 Fetching attendance data on mount for employee:', userData.id);
                    fetchAttendance(userData.id);
                }
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
    }, []);

    const buildAttendanceMap = useCallback((data) => {
        const map = {};

        // Handle different data structures
        let records = [];
        if (data?.monthly && Array.isArray(data.monthly)) {
            records = data.monthly;
        } else if (Array.isArray(data)) {
            records = data;
        } else if (data?.attendance && Array.isArray(data.attendance)) {
            records = data.attendance;
        }

        console.log('🔨 buildAttendanceMap processing:', {
            inputType: typeof data,
            isArray: Array.isArray(data),
            hasMonthly: !!data?.monthly,
            hasAttendance: !!data?.attendance,
            recordsCount: records.length,
            firstRecord: records[0]
        });

        records.forEach(record => {
            if (!record.date) {
                console.warn('⚠️ Record missing date:', record);
                return;
            }
            const dateKey = record.date.split('T')[0];
            map[dateKey] = {
                date: dateKey,
                loginTime: record.check_in_time?.substring(11, 16) || record.loginTime,
                logoutTime: record.check_out_time?.substring(11, 16) || record.logoutTime,
                status: record.status || 'present',
                leaveType: record.leave_type,
                location: record.location?.type || record.location || 'Office',
                workingHours: record.work_hours ?? record.working_hours ?? record.workHours ?? 0,
                updatedAt: record.updated_at || new Date().toISOString()
            };
        });

        console.log('🔨 buildAttendanceMap result:', {
            mapSize: Object.keys(map).length,
            dates: Object.keys(map).sort().slice(0, 5)
        });

        return map;
    }, []);

    // Fetch attendance from backend when authenticated
    const fetchAttendance = useCallback(async (employeeId, forceRefresh = false) => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            //console.log('⚠️ No auth token, cannot fetch attendance');
            return;
        }

        if (!employeeId) {
            //console.log('⚠️ No employee ID provided');
            return;
        }

        // Check cache first if not forcing refresh
        const cacheKey = `attendance_${employeeId}`;
        if (!forceRefresh) {
            const cached = sessionCache.get(cacheKey);
            if (cached) {
                //console.log('📦 Using cached attendance data');
                setAttendanceRecords(prev => ({
                    ...prev,
                    [employeeId]: cached
                }));
                return;
            }
        }

        try {
            setLoading(true);
            const currentDate = new Date();
            const month = currentDate.getMonth();
            const year = currentDate.getFullYear();

            // Check if fetching for current user or another employee
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const isOwnProfile = user.id === employeeId;

            let data;
            if (isOwnProfile) {
                // Fetch own attendance
                console.log('📊 Fetching own attendance:', { employeeId, month: month + 1, year });
                data = await attendanceService.getMyAttendance(month + 1, year);
                console.log('📊 Own attendance response:', { dataLength: data?.length, firstRecord: data?.[0] });
            } else {
                // Fetch another employee's attendance (admin/manager view)
                console.log('📊 Fetching employee attendance:', { employeeId, month: month + 1, year });
                const response = await attendanceService.getEmployeeAttendance(employeeId, month + 1, year);
                console.log('📊 Employee attendance response:', {
                    success: response.success,
                    dataKeys: Object.keys(response.data || {}),
                    fullData: response.data,
                    hasMonthly: !!response.data?.monthly,
                    monthlyLength: response.data?.monthly?.length,
                    hasAttendance: !!response.data?.attendance,
                    attendanceLength: response.data?.attendance?.length,
                    isArray: Array.isArray(response.data)
                });

                // Try different data structures
                if (response.success) {
                    if (response.data.monthly) {
                        data = response.data.monthly;
                    } else if (response.data.attendance) {
                        data = response.data.attendance;
                    } else if (Array.isArray(response.data)) {
                        data = response.data;
                    } else {
                        console.error('❌ Unknown data structure:', response.data);
                        data = [];
                    }
                } else {
                    data = [];
                }
                console.log('📊 Extracted data:', { length: data?.length, firstRecord: data?.[0] });
            }

            const attendanceMap = buildAttendanceMap(data);

            // Cache the data
            sessionCache.set(cacheKey, attendanceMap);
            console.log('💾 Cached attendance data for employee:', employeeId, 'Records:', Object.keys(attendanceMap).length, 'Map keys:', Object.keys(attendanceMap).slice(0, 5));

            setAttendanceRecords(prev => ({
                ...prev,
                [employeeId]: attendanceMap
            }));
            setAttendanceSnapshots(prev => ({
                ...prev,
                [employeeId]: data
            }));

            return data;
        } catch (error) {
            console.error('Error fetching attendance:', error);
        } finally {
            setLoading(false);
        }
    }, [buildAttendanceMap]);

    const syncAttendanceFromResponse = useCallback((employeeId, response) => {
        if (!employeeId || !response) return;

        const attendanceMap = buildAttendanceMap(response);

        setAttendanceRecords(prev => ({
            ...prev,
            [employeeId]: attendanceMap
        }));

        setAttendanceSnapshots(prev => ({
            ...prev,
            [employeeId]: response
        }));

        sessionCache.set(`attendance_${employeeId}`, attendanceMap);
    }, [buildAttendanceMap]);

    const markAttendance = (employeeId, date, loginTime, logoutTime = null, status = 'present', leaveType = null, location = 'Office') => {
        const dateKey = date; // YYYY-MM-DD format
        setAttendanceRecords(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                [dateKey]: {
                    date: dateKey,
                    loginTime,
                    logoutTime,
                    status, // present, absent, half-day, leave, lop, holiday
                    leaveType, // casual, sick, earned, lop, etc.
                    location, // Office, WFH, Remote
                    workingHours: logoutTime ? calculateWorkingHours(loginTime, logoutTime) : 0,
                    updatedAt: new Date().toISOString() // Keep as ISO for timestamp
                }
            }
        }));
    };

    const clockIn = async (employeeId, location = 'Office') => {
        const authToken = localStorage.getItem('authToken');

        // Try backend API first
        if (authToken) {
            try {
                const response = await attendanceService.checkIn({ type: location });

                // Update local state with backend response
                if (response.attendance) {
                    const date = response.attendance.date.split('T')[0];
                    const time = response.attendance.check_in_time?.substring(0, 5);
                    markAttendance(employeeId, date, time, null, 'present', null, location);
                }

                return { success: true, time: response.attendance?.check_in_time, date: response.attendance?.date };
            } catch (error) {
                console.error('Backend clock-in failed, using localStorage:', error);
            }
        }

        // Fallback to localStorage
        const now = new Date();
        const date = getCurrentISTDate();
        const time = now.toTimeString().split(' ')[0].substring(0, 5);
        markAttendance(employeeId, date, time, null, 'present', null, location);
        return { success: true, time, date };
    };

    const clockOut = async (employeeId) => {
        const authToken = localStorage.getItem('authToken');

        // Try backend API first
        if (authToken) {
            try {
                const response = await attendanceService.checkOut({ type: 'Office' });

                // Update local state with backend response
                if (response.attendance) {
                    const date = response.attendance.date.split('T')[0];
                    const time = response.attendance.check_out_time?.substring(0, 5);
                    updateLogout(employeeId, date, time);
                }

                return { success: true, time: response.attendance?.check_out_time, date: response.attendance?.date };
            } catch (error) {
                console.error('Backend clock-out failed, using localStorage:', error);
            }
        }

        // Fallback to localStorage
        const now = new Date();
        const date = getCurrentISTDate();
        const time = now.toTimeString().split(' ')[0].substring(0, 5);
        updateLogout(employeeId, date, time);
        return { success: true, time, date };
    };

    const getTodayAttendance = (employeeId) => {
        const today = getCurrentISTDate();
        return getAttendanceByDate(employeeId, today);
    };

    const updateLogout = (employeeId, date, logoutTime) => {
        const dateKey = date;
        setAttendanceRecords(prev => {
            const employeeRecords = prev[employeeId] || {};
            const dayRecord = employeeRecords[dateKey];

            if (!dayRecord) return prev;

            return {
                ...prev,
                [employeeId]: {
                    ...employeeRecords,
                    [dateKey]: {
                        ...dayRecord,
                        logoutTime,
                        workingHours: calculateWorkingHours(dayRecord.loginTime, logoutTime),
                        updatedAt: new Date().toISOString() // Keep as ISO for timestamp
                    }
                }
            };
        });
    };

    const calculateWorkingHours = (loginTime, logoutTime) => {
        if (!loginTime || !logoutTime) return 0;

        const login = new Date(`2000-01-01T${loginTime}`);
        const logout = new Date(`2000-01-01T${logoutTime}`);

        const diffMs = logout - login;
        const diffHours = diffMs / (1000 * 60 * 60);

        return Math.max(0, diffHours).toFixed(2);
    };

    const getEmployeeAttendance = (employeeId, month, year) => {
        const employeeRecords = attendanceRecords[employeeId] || {};

        return Object.values(employeeRecords).filter(record => {
            const recordDate = new Date(record.date);
            return recordDate.getMonth() === month && recordDate.getFullYear() === year;
        }).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const getAttendanceByDate = (employeeId, date) => {
        return attendanceRecords[employeeId]?.[date] || null;
    };

    const getMonthlyStats = (employeeId, month, year) => {
        const records = getEmployeeAttendance(employeeId, month, year);

        const stats = {
            totalDays: records.length,
            present: 0,
            absent: 0,
            halfDay: 0,
            leave: 0,
            totalWorkingHours: 0
        };

        records.forEach(record => {
            if (record.status === 'present') stats.present++;
            else if (record.status === 'absent') stats.absent++;
            else if (record.status === 'half-day') stats.halfDay++;
            else if (record.status === 'leave') stats.leave++;

            stats.totalWorkingHours += parseFloat(record.workingHours || 0);
        });

        return stats;
    };

    const getAllEmployeesAttendance = (date) => {
        const result = {};
        Object.keys(attendanceRecords).forEach(employeeId => {
            const record = attendanceRecords[employeeId][date];
            if (record) {
                result[employeeId] = record;
            }
        });
        return result;
    };

    const submitAttendanceRequest = async (requestData) => {
        try {
            const newRequest = {
                id: Date.now(),
                ...requestData,
                status: 'Pending',
                submittedAt: new Date().toISOString()
            };

            setAttendanceRequests(prev => [...prev, newRequest]);
            return { success: true, message: 'Attendance request submitted successfully' };
        } catch (error) {
            return { success: false, message: 'Failed to submit attendance request' };
        }
    };

    const getAttendanceRequests = (employeeId) => {
        return attendanceRequests.filter(req => req.employeeId === employeeId);
    };

    const getAttendanceForPeriod = (employeeId, startDate, endDate) => {
        const employeeRecords = attendanceRecords[employeeId] || {};
        const start = new Date(startDate);
        const end = new Date(endDate);

        return Object.values(employeeRecords).filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= start && recordDate <= end;
        }).sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    const getAttendanceStats = (employeeId, period = 30) => {
        const employeeRecords = attendanceRecords[employeeId] || {};
        const records = Object.values(employeeRecords);

        const today = new Date();
        const periodStart = new Date(today);
        periodStart.setDate(periodStart.getDate() - period);

        const periodRecords = records.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= periodStart && recordDate <= today;
        });

        const totalHours = periodRecords.reduce((sum, record) => sum + parseFloat(record.workingHours || 0), 0);
        const avgHours = periodRecords.length > 0 ? (totalHours / periodRecords.length).toFixed(1) : 0;

        const onTimeCount = periodRecords.filter(record => {
            if (!record.loginTime) return false;
            const [hours] = record.loginTime.split(':').map(Number);
            return hours <= 9; // On time if login before or at 9 AM
        }).length;

        const onTimePercentage = periodRecords.length > 0 ? ((onTimeCount / periodRecords.length) * 100).toFixed(0) : 0;

        return {
            avgHoursPerDay: avgHours,
            onTimeArrivalPercentage: onTimePercentage,
            totalWorkingDays: periodRecords.length,
            totalHours: totalHours.toFixed(1)
        };
    };

    // Clear attendance cache - useful after regularization approval
    const clearAttendanceCache = useCallback((employeeId = null) => {
        console.log('🔄 Clearing attendance cache', employeeId ? `for employee: ${employeeId}` : 'for all');
        if (employeeId) {
            sessionCache.clear(`attendance_${employeeId}`);
            setAttendanceRecords(prev => {
                const newRecords = { ...prev };
                delete newRecords[employeeId];
                return newRecords;
            });
        } else {
            // Clear all attendance cache entries
            sessionCache.clearAll();
            setAttendanceRecords({});
        }
    }, []);

    const value = {
        attendanceRecords,
        attendanceSnapshots,
        attendanceRequests,
        loading,
        fetchAttendance,
        syncAttendanceFromResponse,
        clearAttendanceCache,
        markAttendance,
        updateLogout,
        clockIn,
        clockOut,
        getTodayAttendance,
        getEmployeeAttendance,
        getAttendanceByDate,
        getMonthlyStats,
        getAllEmployeesAttendance,
        submitAttendanceRequest,
        getAttendanceRequests,
        getAttendanceForPeriod,
        getAttendanceStats
    };

    return (
        <AttendanceContext.Provider value={value}>
            {children}
        </AttendanceContext.Provider>
    );
};
